import type { FlowPostResponse, VisibilityTypes } from '@assembly-web/services';
import { MemberState } from '@assembly-web/services';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import {
  EyeIcon,
  EyeSlashIcon,
  UserGroupIcon,
} from '@heroicons/react/24/solid';
import { defineMessages, useIntl } from 'react-intl';
import { twJoin } from 'tailwind-merge';

import { Avatar, AvatarSize } from '../../../DesignSystem/Feedback/Avatar';
import { TextStyle } from '../../../DesignSystem/Feedback/TextStyle';
import { Tooltip } from '../../../DesignSystem/Feedback/Tooltip';
import { classNames } from '../../../DesignSystem/Utils/classNames';
import type { PostCardProps } from './PostCard';
import { PostCardActions } from './PostCard';
import { getTimeAgo } from './utils';

export const getVisibilityIcon = (visibility: VisibilityTypes) => {
  if (visibility === 'custom' || visibility === 'onlyParticipants') {
    return <UserGroupIcon />;
  }
  if (visibility === 'private' || visibility === 'onlyOwners') {
    return <EyeSlashIcon />;
  }
  return <EyeIcon />;
};

export const mapHexCodeToEmoticon = (value: string) => {
  try {
    if (value.indexOf('-') > 0) {
      return String.fromCodePoint(
        ...value.split('-').map((item) => +`0x${item}`)
      );
    }

    return String.fromCodePoint(+`0x${value}`);
  } catch (e) {
    return value;
  }
};

const messages = defineMessages({
  postedIn: {
    defaultMessage: 'posted in',
    id: '19BOio',
  },
  ago: {
    defaultMessage: '{timeCreatedAt} ago',
    id: 'Pzwk7p',
  },
  edited: {
    defaultMessage: 'Edited',
    id: 'OhIqLr',
  },
  anonymous: {
    defaultMessage: 'Anonymous',
    id: 'LXxsbk',
  },
  deactivatedUser: {
    defaultMessage: '(deactivated)',
    id: 'osOx6w',
  },
});

export function PostCardHeader({
  respondent,
  flowName,
  createdAt,
  visibility,
  flowIcon,
  onPostCardHeaderClick,
  openViewInsightsModal,
  flowId,
  isEdited,
  showFlowDetails = true,
}: {
  respondent: FlowPostResponse['respondent'];
  flowName: FlowPostResponse['flow']['name'];
  createdAt: FlowPostResponse['createdAt'];
  visibility: FlowPostResponse['visibility'];
  flowIcon: FlowPostResponse['flow']['icon']['value'];
  onPostCardHeaderClick: PostCardProps['onPostCardHeaderClick'];
  flowId: string;
  isEdited: boolean;
  showFlowDetails?: boolean;
  openViewInsightsModal?: () => void;
}) {
  const { formatMessage } = useIntl();

  let authorButton = (
    <button
      className={classNames('mr-1 flex', {
        'text-primary-6 hover:text-primary-5':
          respondent !== null &&
          respondent.memberID !== '' &&
          respondent.memberState !== MemberState.Deactivated,
        'cursor-default': respondent === null || respondent.memberID === '',
        'text-gray-6': respondent?.memberState === MemberState.Deactivated,
      })}
      disabled={respondent?.memberState === MemberState.Deactivated}
      onClick={() => {
        if (respondent !== null && respondent.memberID !== '')
          onPostCardHeaderClick(
            PostCardActions.PersonClicked,
            respondent.memberID
          );
      }}
    >
      <TextStyle
        variant="sm-medium"
        className={twJoin(
          respondent?.memberState === MemberState.Deactivated && 'text-gray-8'
        )}
      >
        {respondent === null
          ? formatMessage(messages.anonymous)
          : `${respondent.firstName} ${respondent.lastName}`}
      </TextStyle>

      {respondent?.memberState === MemberState.Deactivated && (
        <TextStyle variant="sm-regular" className="ml-1 text-gray-8">
          {formatMessage(messages.deactivatedUser)}
        </TextStyle>
      )}
    </button>
  );

  if (respondent?.pronouns) {
    authorButton = (
      <Tooltip tooltipText={respondent.pronouns}>{authorButton}</Tooltip>
    );
  }

  return (
    <header className="flex">
      {respondent === null ? (
        <div className="h-10 w-10 rounded-full bg-error-2" />
      ) : (
        <Avatar
          size={AvatarSize.Large}
          image={respondent.image}
          name={respondent.firstName}
          memberID={respondent.memberID}
          className={twJoin(
            respondent.memberState === MemberState.Deactivated && 'grayscale'
          )}
        />
      )}
      <div className="flex flex-col justify-between pl-2 ">
        <div className="flex flex-row flex-wrap text-sm font-normal">
          {authorButton}
          {showFlowDetails && (
            <>
              {formatMessage(messages.postedIn)}
              <button
                onClick={() => {
                  if (flowId)
                    onPostCardHeaderClick(
                      PostCardActions.FlowNameClicked,
                      flowId
                    );
                }}
                className="mx-1 text-primary-6 hover:text-primary-5"
              >
                <TextStyle variant="sm-medium">
                  {mapHexCodeToEmoticon(flowIcon)} {flowName}
                </TextStyle>
              </button>
            </>
          )}
        </div>
        <div className="flex items-center gap-1 text-xs text-gray-8">
          <span>
            {formatMessage(messages.ago, {
              timeCreatedAt: getTimeAgo(new Date(createdAt)),
            })}{' '}
            •
          </span>
          <span className="h-4 w-4">{getVisibilityIcon(visibility)}</span>
          {openViewInsightsModal && (
            <>
              <span>•</span>
              <button
                className="h-4 w-4"
                onClick={openViewInsightsModal}
                aria-label="View insights"
              >
                <ChartBarIcon />
              </button>
            </>
          )}
          {isEdited && (
            <TextStyle variant="xs-regular" subdued>
              {formatMessage(messages.edited)}
            </TextStyle>
          )}
        </div>
      </div>
    </header>
  );
}
